import {Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser, NgClass} from "@angular/common";
import {NavigationEnd, Router, RouterLink, RouterOutlet} from "@angular/router";
import {env} from "../../../environments/environment";
import {NAVS} from "../../constants/navs";
import {APP_ROUTES} from "../../constants/routes";
import {filter, Subscription} from "rxjs";
import {LucideDynamicIcon} from "@lucide/angular";
import {APP_LUCIDE_ICONS} from "../../icons/lucide-icons";
import {AnalyticsService} from "../../services/analytics.service";

@Component({
    selector: 'app-main-layout',
    templateUrl: './main-layout.component.html',
    standalone: true,
    imports: [
        NgClass,
        RouterOutlet,
        RouterLink,
        LucideDynamicIcon,
    ],
    styleUrls: ['./main-layout.component.scss']
})
export class MainLayoutComponent implements OnInit, OnDestroy {
    protected isFeaturesMenuOpen = false;
    protected isToolsMenuOpen = false;
    protected isNavbarMenuOpen = false;
    protected suppressFeaturesHover = false;
    protected suppressToolsHover = false;
    private routerSubscription?: Subscription;

    constructor(
        private router: Router,
        private analytics: AnalyticsService,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngOnInit(): void {
        let currentPath = this.getPathWithoutFragment(this.router.url);

        this.routerSubscription = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                const nextPath = this.getPathWithoutFragment(event.urlAfterRedirects);

                this.closeAllMenus();
                if (nextPath !== currentPath && isPlatformBrowser(this.platformId)) {
                    window.scrollTo(0, 0);
                }

                currentPath = nextPath;
            });
    }

    ngOnDestroy() {
        this.routerSubscription?.unsubscribe();
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeAllMenus();
    }

    @HostListener('document:mousemove')
    onDocumentMouseMove(): void {
        if (this.suppressFeaturesHover) {
            this.suppressFeaturesHover = false;
        }

        if (this.suppressToolsHover) {
            this.suppressToolsHover = false;
        }
    }

    protected keepNavbarMenuOpen(event: Event): void {
        event.stopPropagation();
    }

    protected toggleNavbarMenu(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.isNavbarMenuOpen = !this.isNavbarMenuOpen;

        if (!this.isNavbarMenuOpen) {
            this.isFeaturesMenuOpen = false;
            this.isToolsMenuOpen = false;
        }
    }

    protected toggleFeaturesMenu(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.isFeaturesMenuOpen = !this.isFeaturesMenuOpen;
        this.isToolsMenuOpen = false;
    }

    protected closeFeaturesMenu(): void {
        this.isFeaturesMenuOpen = false;
    }

    protected toggleToolsMenu(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.isToolsMenuOpen = !this.isToolsMenuOpen;
        this.isFeaturesMenuOpen = false;
    }

    protected handleNavigationClick(): void {
        this.suppressFeaturesHover = true;
        this.suppressToolsHover = true;
        this.closeAllMenus();
    }

    protected registrationUrl(source: string): string {
        return this.analytics.registrationUrl(source);
    }

    protected trackRegistrationClick(source: string): void {
        this.analytics.goal('registration_click', {source});
        this.handleNavigationClick();
    }

    protected trackLoginClick(source: string): void {
        this.analytics.goal('lk_click', {source, action: 'login'});
        this.handleNavigationClick();
    }

    private closeAllMenus(): void {
        this.isFeaturesMenuOpen = false;
        this.isToolsMenuOpen = false;
        this.isNavbarMenuOpen = false;
    }

    private getPathWithoutFragment(url: string): string {
        return url.split('#')[0];
    }

    protected readonly currentYear = new Date().getFullYear();
    protected readonly env = env;
    protected readonly APP_ROUTES = APP_ROUTES;
    protected readonly APP_LUCIDE_ICONS = APP_LUCIDE_ICONS;
    protected readonly NAVS = NAVS;
}
